/**
 * Abtretung process overview screen
 *
 * Route: `/webAppOnboarding/insurance-abtretung-process-overview`. Explains assignment
 * billing; forwards to install.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('abtretung')`; `useSaveOnboardingStep(...)`.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { space } from '../../../styles/onboardingUiTokens';
import { ListTimeline, ListTimelineItem } from '../components/content/ListTimeline';
import { SvgIcon } from '../components/icons/SvgIcon';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { H1 } from '../components/typography/HeadingLevels';
import { useInsuranceExplainBillingGuard } from '../hooks/useInsuranceExplainBillingGuard';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;

export function WebOnboardingInsuranceAbtretungProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_abtretung_process_overview');
  const posthog = usePostHog();
  const navigate = useNavigate();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );
  const abtretungExplain = ctx.meta.abtretungExplain;
  const insuranceName = ctx.insuranceName ?? '';

  useInsuranceExplainBillingGuard('abtretung');

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'abtretung',
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
    if (ctx.forwardPath) {
      navigate(ctx.forwardPath);
    }
  };

  const bodyContent = (() => {
    if (!insuranceName || !abtretungExplain) {
      return null;
    }

    const { timeline } = abtretungExplain;

    return (
      <ContentColumn>
        <H1>{abtretungExplain.headlineTemplate}</H1>
        <ListTimeline>
          <ListTimelineItem
            // marker={<SvgIcon name="check" size={20} primaryColor={colors.dark} />}
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                💪
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.white}
            title={timeline.step1Title}
          >
            {timeline.step1Body}
          </ListTimelineItem>
          <ListTimelineItem
            marker={<SvgIcon name="check" size={20} primaryColor={colors.white} />}
            // marker={<span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>📨</span>}
            circleBackground={colors.success}
            circleBorder={colors.success}
            numberColor={colors.white}
            title={timeline.step2Title}
          >
            {timeline.step2BodyTemplate}
          </ListTimelineItem>
        </ListTimeline>
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={5}
      onBack={handleBack}
      forwardLabel="Starten"
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingBody>{bodyContent}</OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
